import React, { useMemo, useState } from "react";
import { Plus, Receipt } from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import StoreShell from "../components/StoreShell";
import StockActionModal from "../components/StockActionModal";
import { fmtDate, fmtQty, monthRange, recipient } from "../utils/stores";
import { useStoreGetMovementsQuery } from "../api/storesApi";

const naira = (n: number) =>
  `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/**
 * Sales — what staff have sold out of what they are holding.
 *
 * A sale debits a holding, never a store balance: the stock left the store
 * when it was issued. So this is the counter's day book, not the store's.
 */
function StoreSalesInner() {
  const thisMonth = monthRange();
  const [from, setFrom] = useState(thisMonth.from);
  const [to, setTo] = useState(thisMonth.to);
  const [selling, setSelling] = useState(false);

  const { data, isLoading, error } = useStoreGetMovementsQuery({ type: "sale" as any, from, to });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const totals = useMemo(() => ({
    amount: rows.reduce((n: number, r: any) => n + Number(r.amount ?? 0), 0),
    units: rows.reduce((n: number, r: any) => n + Number(r.quantity ?? 0), 0),
  }), [rows]);

  return (
    <StoreShell
      title="Sales"
      subtitle="What staff have sold from the stock they are holding."
      actions={
        <AccessGate perm={PERMS.store.movement}>
          <button
            onClick={() => setSelling(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg"
          >
            <Plus className="w-4 h-4" /> New sale
          </button>
        </AccessGate>
      }
    >
      <div className="bg-white border rounded-xl p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100" />
          </div>

          <div className="ml-auto flex gap-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-2">
              <div className="text-lg font-bold text-emerald-700">{naira(totals.amount)}</div>
              <div className="text-[11px] text-emerald-600 uppercase tracking-wider">Taken</div>
            </div>
            <div className="rounded-lg bg-gray-50 px-4 py-2">
              <div className="text-lg font-bold text-gray-800">{fmtQty(totals.units)}</div>
              <div className="text-[11px] text-gray-400 uppercase tracking-wider">Units sold</div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">
          {describeApiError(error, "Could not load the sales.")}
        </div>
      )}

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-14 text-center text-sm text-gray-400 animate-pulse">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center px-6">
            <Receipt className="w-9 h-9 text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500">No sales in this period</p>
            <p className="text-xs text-gray-400 mt-1">
              A sale is recorded against the stock a member of staff is holding — issue stock
              to them first.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Item</th>
                  <th className="px-4 py-2 font-medium">Sold by</th>
                  <th className="px-4 py-2 font-medium">Sold to</th>
                  <th className="px-4 py-2 font-medium text-right">Qty</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r: any) => (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(r.movement_date)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.item?.name ?? "—"}</div>
                      <div className="text-xs text-gray-400 font-mono">{r.item?.code}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{recipient(r)}</td>
                    <td className="px-4 py-3 text-gray-700">{r.sold_to || "—"}</td>
                    <td className="px-4 py-3 text-right text-gray-800">
                      {fmtQty(r.quantity)} <span className="text-gray-400 text-xs">{r.item?.unit}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {r.amount != null ? naira(r.amount) : "—"}
                      {r.unit_price != null && (
                        <div className="text-[11px] text-gray-400">at {naira(r.unit_price)}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selling && <StockActionModal action="sale" onClose={() => setSelling(false)} />}
    </StoreShell>
  );
}

export default function StoreSalesPage() {
  return (
    <PageGate perm={PERMS.store.access}>
      <StoreSalesInner />
    </PageGate>
  );
}
